import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coins, Shield, Plus, User, Search, Ticket, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function AdminUsers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_email: "", amount: 500 });
  const [tokenOpen, setTokenOpen] = useState(false);
  const [tokenForm, setTokenForm] = useState({ user_email: "", item_type: "talentshow_free_entry", qty: 1 });
  const [deleteTarget, setDeleteTarget] = useState(null);   // { id, email, display_name } or null
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [jumpPage, setJumpPage] = useState("");

  // Any filter change resets to page 1.
  useEffect(() => { setPage(1); }, [search, role, sort]);

  const { data, isFetching } = useQuery({
    queryKey: ["admin-users", search, role, sort, page],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (search) p.set("q", search);
      if (role !== "all") p.set("role", role);
      p.set("sort", sort);
      p.set("page", String(page));
      p.set("page_size", String(PAGE_SIZE));
      return (await api.get(`/admin/users?${p}`)).data;
    },
    keepPreviousData: true,
  });
  const users = data?.items || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const grant = useMutation({
    mutationFn: async () => (await api.post("/admin/grant-credits", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); setOpen(false); toast.success("Credits granted"); },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });
  const grantToken = useMutation({
    mutationFn: async () => (await api.post("/inventory/admin/grant", tokenForm)).data,
    onSuccess: () => { setTokenOpen(false); toast.success("Token granted"); },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });
  const removeUser = useMutation({
    mutationFn: async (userId) => (await api.delete(`/admin/users/${userId}`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setDeleteTarget(null);
      const cascade = data?.cascade || {};
      const scrubbed = Object.entries(cascade).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`User removed${scrubbed ? ` · scrubbed ${scrubbed}` : ""}`);
    },
    onError: (e) => toast.error(apiError(e, "Couldn't remove user")),
  });

  const goToPage = () => {
    const n = Number(jumpPage);
    if (Number.isFinite(n) && n >= 1 && n <= pages) setPage(n);
    setJumpPage("");
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">USERS</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="admin-grant-credits-button" className="bg-amber-400 text-black hover:bg-amber-300"><Plus className="h-4 w-4 mr-1" /> Grant Credits</Button></DialogTrigger>
          <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-[0.08em]">GRANT CREDITS</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">User Email</Label>
                <Input data-testid="grant-email-input" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Amount</Label>
                <Input data-testid="grant-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <Button data-testid="grant-confirm-button" onClick={() => grant.mutate()} disabled={!form.user_email || grant.isPending} className="w-full bg-amber-400 text-black hover:bg-amber-300">Grant</Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={tokenOpen} onOpenChange={setTokenOpen}>
          <DialogTrigger asChild><Button data-testid="admin-grant-token-button" variant="outline" className="border-orange-400/40 text-orange-200 hover:bg-orange-400/10"><Ticket className="h-4 w-4 mr-1" /> Grant Token</Button></DialogTrigger>
          <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-[0.08em]">GRANT TOKEN</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">User Email</Label>
                <Input data-testid="grant-token-email-input" value={tokenForm.user_email} onChange={(e) => setTokenForm({ ...tokenForm, user_email: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Token Type</Label>
                <Select value={tokenForm.item_type} onValueChange={(v) => setTokenForm({ ...tokenForm, item_type: v })}>
                  <SelectTrigger data-testid="grant-token-type-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                    <SelectItem value="talentshow_free_entry">🎟️ Talent Show Free Entry</SelectItem>
                    <SelectItem value="hq_enhancement">✨ HQ Enhancement Token</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Quantity</Label>
                <Input data-testid="grant-token-qty-input" type="number" min={1} max={999} value={tokenForm.qty} onChange={(e) => setTokenForm({ ...tokenForm, qty: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <Button data-testid="grant-token-confirm-button" onClick={() => grantToken.mutate()} disabled={!tokenForm.user_email || grantToken.isPending} className="w-full bg-orange-400 text-black hover:bg-orange-300">Grant Token</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input data-testid="admin-users-search" placeholder="Search by email or username…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[hsl(220_18%_12%)] border-white/10" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="admin-users-role-filter" className="w-40 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="collector">Collector</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="admin-users-sort" className="w-48 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="credits_desc">Credits: high → low</SelectItem>
            <SelectItem value="credits_asc">Credits: low → high</SelectItem>
            <SelectItem value="name_asc">Name: A → Z</SelectItem>
          </SelectContent>
        </Select>
        <span data-testid="admin-users-total" className="font-mono text-[11px] text-white/40 ml-auto">{total} user{total === 1 ? "" : "s"}</span>
      </div>

      <div className="hud-pill rounded-2xl overflow-hidden" data-testid="admin-users-table">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
            <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Credits</th><th className="px-4 py-3">Cards</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className={isFetching ? "opacity-50 transition-opacity" : "transition-opacity"}>
            {users.map((u) => (
              <tr key={u.id} data-testid={`admin-users-row-${u.id}`} className="border-t border-white/5">
                <td className="px-4 py-3 text-white flex items-center gap-2"><User className="h-3.5 w-3.5 text-white/40" /> {u.display_name}</td>
                <td className="px-4 py-3 text-white/70">{u.email}</td>
                <td className="px-4 py-3">{u.role === "admin" ? <span className="text-emerald-300 inline-flex items-center gap-1"><Shield className="h-3 w-3" /> Admin</span> : <span className="text-white/50">Collector</span>}</td>
                <td className="px-4 py-3 font-mono text-amber-300"><Coins className="h-3.5 w-3.5 inline mr-1" /> {u.credits.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-white/70">{u.cards_count}</td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="ghost" onClick={() => { setForm({ user_email: u.email, amount: 500 }); setOpen(true); }} className="text-amber-300 text-xs">+ Credits</Button>
                  <Button size="sm" variant="ghost" data-testid={`grant-token-${u.id}`} onClick={() => { setTokenForm({ user_email: u.email, item_type: "talentshow_free_entry", qty: 1 }); setTokenOpen(true); }} className="text-orange-300 text-xs">+ Token</Button>
                  {u.role !== "admin" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`admin-users-remove-${u.id}`}
                      onClick={() => setDeleteTarget({ id: u.id, email: u.email, display_name: u.display_name })}
                      className="text-red-300 hover:text-red-200 hover:bg-red-500/10 text-xs"
                      title="Remove user"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No users match.</td></tr>}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="admin-users-pagination">
          <span className="font-mono text-[11px] text-white/40">Page {page} of {pages}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid="admin-users-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="border-white/15 text-white/70 disabled:opacity-30">
              <ChevronLeft className="h-4 w-4 mr-1" /> Prev
            </Button>
            <Button size="sm" variant="outline" data-testid="admin-users-next" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="border-white/15 text-white/70 disabled:opacity-30">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            <div className="flex items-center gap-1.5 ml-2">
              <Input
                data-testid="admin-users-jump-input"
                type="number"
                min={1}
                max={pages}
                placeholder="Go to…"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") goToPage(); }}
                className="w-20 h-8 bg-[hsl(220_18%_12%)] border-white/10 text-xs"
              />
              <Button size="sm" variant="outline" data-testid="admin-users-jump-go" onClick={goToPage} className="border-white/15 text-white/70 h-8">Go</Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove-user confirmation. Destructive action + cascade summary so
          admin knows exactly what will disappear before hitting Confirm. */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-[hsl(222_22%_8%)] border-red-400/30 text-white" data-testid="admin-users-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl tracking-[0.08em] text-red-200 flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> REMOVE USER
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70 text-sm space-y-2 pt-2">
              <span className="block">
                You&apos;re about to permanently remove <span className="font-mono text-white">{deleteTarget?.display_name}</span> <span className="text-white/50">({deleteTarget?.email})</span>.
              </span>
              <span className="block text-red-300/90 text-[13px]">
                This will also delete their cards, credit ledger entries, active sessions, inventory tokens, sports bets, path activity, ToS acceptance history, age confirmations, and any print quotes.
              </span>
              <span className="block text-white/50 text-[12px]">
                The audit log entry will be preserved. This cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-users-remove-cancel" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-users-remove-confirm"
              disabled={removeUser.isPending}
              onClick={() => deleteTarget && removeUser.mutate(deleteTarget.id)}
              className="bg-red-500 text-white hover:bg-red-400 focus:ring-red-400"
            >
              {removeUser.isPending ? "Removing…" : "Yes — remove permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
